import { gql } from "lib/apolloClient";
import { curriculumProgramFragment } from "modules/CommonFragments";
import { defaultOutcomeFragment } from "./BehaviourManagementFragments";
import { incidentFragment } from "Incident/modules/IncidentFragments";

export const getBehaviourLocationsQuery = gql`
  query getBehaviourLocations(
    $id: ID!
    $locationFilters: BehaviourLocationFilters
  ) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        behaviourLocations(filters: $locationFilters) {
          id
          name
        }
      }
    }
  }
`;

export const getBehaviourCategoriesQuery = gql`
  query getBehaviourCategories($id: ID!, $filters: BehaviourCategoryFilters) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        behaviourCategories(filters: $filters) {
          id
          name
          iconData
          color
          level
          sentiment {
            id
            label
            value
          }
          grades {
            id
            name
          }
          curriculumPrograms {
            id
          }
        }
      }
    }
  }
`;

export const getBehaviourSeveritiesQuery = gql`
  query getBehaviourSeverities(
    $id: ID!
    $filters: BehaviourSeverityFilters
    $includeUserCurriculums: Boolean = false
    $includeDefaultOutcomes: Boolean = true
  ) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        behaviourSeverities(filters: $filters) {
          id
          level
          name
          color
          defaultOutcomes @include(if: $includeDefaultOutcomes) {
            ...defaultOutcomeItem
          }
        }
      }
    }
  }
  ${defaultOutcomeFragment.defaultOutcome}
`;

export const getDefaultOutcomeBySeverityAndCategoryQuery = gql`
  query getDefaultOutcomeBySeverityAndCategory(
    $id: ID!
    $filters: BehaviourSeverityFilters
    $defaultOutcomeFilters: BehaviourDefaultOutcomeFilters
    $includeUserCurriculums: Boolean = false
  ) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        behaviourSeverities(filters: $filters) {
          id
          defaultOutcomes(filters: $defaultOutcomeFilters) {
            ...defaultOutcomeItem
          }
        }
      }
    }
  }
  ${defaultOutcomeFragment.defaultOutcome}
`;

export const getMultiLevelBehaviourCategoryQuery = gql`
  query getMultiLevelBehaviourCategoryNodes(
    $id: ID!
    $type: MULTI_LEVEL_NODE_TYPE!
    $behaviourCategoryFilters: BehaviourCategoryFilters
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        getMultiLevelNodes(
          input: {
            type: $type
            behaviourCategoryFilters: $behaviourCategoryFilters
          }
        ) {
          type
          rootNodes
          nodes {
            id
            ... on BehaviourCategory {
              name
              color
              parentCategory {
                id
                name
              }
              children
              level
              rootCategory {
                id
                name
                iconData
                color
                sentiment {
                  id
                  label
                }
                curriculumPrograms {
                  id
                }
              }
            }
          }
          draftId
        }
      }
    }
  }
`;

export const getBehaviourActionsQuery = gql`
  query getBehaviourActionsQuery($id: ID!, $filters: BehaviourActionFilters) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        behaviourActions(filters: $filters) {
          id
          title
          type
          state
          metadata
        }
      }
    }
  }
`;

export const getStaffListQuery = gql`
  query getStaffList(
    $organizationId: ID!
    $first: Int
    $after: String
    $filters: OrgConnectionFilterInput
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        staff(first: $first, after: $after, filters: $filters) {
          totalCount
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              ... on Staff {
                id
                prefix
                suffix
                firstName
                preferredName
                middleName
                lastName
                pronouns
                profileImage
                email
                roles
                type
                role
                curriculumPrograms {
                  ...curriculumProgramBasicDetailsItem
                }
              }
            }
          }
        }
      }
    }
  }
  ${curriculumProgramFragment.curriculumProgramBasicDetails}
`;

export const getStaffListByIdQuery = gql`
  query getStaffListById($ids: [ID!]) {
    multiNode(ids: $ids, type: STAFF) {
      id
      ... on Staff {
        id
        firstName
        middleName
        lastName
        profileImage
        preferredName
        prefix
        suffix
        pronouns
        email
        type
        role
        curriculumPrograms {
          ...curriculumProgramBasicDetailsItem
        }
      }
    }
  }
  ${curriculumProgramFragment.curriculumProgramBasicDetails}
`;

export const getStaffCoursesQuery = gql`
  query getStaffCourses(
    $id: ID!
    $first: Int
    $after: String
    $filters: CourseFilter
  ) {
    node(id: $id, type: STAFF) {
      id
      ... on Staff {
        coursesV2(first: $first, after: $after, filters: $filters) {
          totalCount
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              id
              title
              curriculumProgram {
                id
                acronym
              }
            }
          }
        }
      }
    }
  }
`;

export const getIncidentFeedQuery = gql`
  query getBehaviourIncidentFeed(
    $id: ID!
    $input: BehaviourIncidentFeedInput
    $accessibleUsersInput: BehaviourAccessibleUsersInput!
    $includeAccessibleUsers: Boolean = false
  ) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        behaviourIncidentFeed(input: $input) {
          totalCount
          stateWiseCount {
            draft
            published
            resolved
          }
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              ...incidentDetails
              ... on BehaviourIncident {
                id
                accessibleUsers(input: $accessibleUsersInput)
                  @include(if: $includeAccessibleUsers) {
                  userType
                  sharedWithUser
                  totalCount
                }
              }
            }
          }
        }
      }
    }
  }
  ${incidentFragment.incidentDetailsFragment}
`;

export const getIncidentAccessibleUsersQuery = gql`
  query getIncidentAccessibleUsers(
    $id: ID!
    $input: BehaviourAccessibleUsersInput!
  ) {
    node(id: $id, type: BEHAVIOUR_INCIDENT) {
      ... on BehaviourIncident {
        id
        accessibleUsers(input: $input) {
          userType
          sharedWithUser
          totalCount
          users {
            pageInfo {
              hasNextPage
              hasPreviousPage
              endCursor
            }
            edges {
              node {
                id
                firstName
                middleName
                profileImage
                lastName
                ... on Student {
                  pronouns
                  suffix
                  preferredName
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getStateWiseCountQuery = gql`
  query getStateWiseCount($id: ID!, $input: BehaviourIncidentFeedInput) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        behaviourIncidentFeed(input: $input) {
          stateWiseCount {
            draft
            published
            resolved
          }
        }
      }
    }
  }
`;

export const getStaffGradesQuery = gql`
  query getStaffGrades($id: ID!, $filters: AllGradesFilters) {
    node(id: $id, type: STAFF) {
      id
      ... on Staff {
        allGrades(filters: $filters) {
          id
          name
          curriculumProgram {
            id
            acronym
          }
        }
      }
    }
  }
`;

export const getStudentBehaviourOverviewQuery = gql`
  query getStudentBehaviourOverview(
    $id: ID!
    $behaviourOverviewInput: BehaviourOverviewInput
  ) {
    node(id: $id, type: STUDENT) {
      ... on Student {
        id
        firstName
        middleName
        lastName
        email
        type
        behaviourOverview(input: $behaviourOverviewInput) {
          monthWiseCount {
            year
            month
            categoryWiseCount {
              count
              category {
                id
                name
                color
              }
            }
          }
          categoryWiseCount {
            category {
              id
              name
              color
            }
            count
          }
        }
      }
    }
  }
`;

export const getBehaviourIncidentExportQuery = gql`
  query getBehaviourIncidentExport($input: BehaviourIncidentExportInput) {
    platform {
      organization {
        behaviourIncidentExport(input: $input) {
          url
        }
      }
    }
  }
`;

export const getCurriculumBehaviourAdminsQuery = gql`
  query getCurriculumBehaviourAdmins($id: ID!, $filters: ModuleAdminsFilters!) {
    node(id: $id, type: ORGANIZATION) {
      ... on Organization {
        id
        moduleAdminsFeed(filters: $filters) {
          id
          staffIds
          curriculumProgram {
            id
          }
        }
      }
    }
  }
`;

export const getACLPermissionStaffQuery = gql`
  query getStaffsBasedOnPermissionAndContexts(
    $permissionId: ID!
    $contexts: [ACLContextEntityInput!]
    $academicYearId: ID
  ) {
    node(id: $permissionId, type: ACL_CONTROL_PARAMETER) {
      ... on ACLPermission {
        id
        type
        permission
        enabledUsers(context: $contexts, academicYearId: $academicYearId) {
          ... on Staff {
            id
            firstName
            middleName
            lastName
            profileImage
            email
          }
        }
      }
    }
  }
`;

export const getACLPermissionStaffQueryV2 = gql`
  query getPermissionEnabledUsers(
    $id: ID!
    $first: Int
    $after: String
    $searchText: String
    $academicYearId: ID
    $enabledUsersFilter: ACLEnabledUsersFilterInput!
  ) {
    node(id: $id, type: ACL_CONTROL_PARAMETER) {
      ... on ACLPermission {
        id
        uid
        enabledUsersV2(
          first: $first
          after: $after
          searchText: $searchText
          filters: $enabledUsersFilter
          academicYearId: $academicYearId
        ) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              ... on Staff {
                id
                firstName
                middleName
                lastName
                profileImage
                preferredName
                pronouns
                prefix
                suffix
                email
                type
              }
            }
          }
        }
      }
    }
  }
`;

export const getACLRoleAssignedUsersQuery = gql`
  query getStaffMembersMappedToRole(
    $roleId: ID!
    $after: String
    $first: Int
    $orderBy: ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
    $academicYearId: ID!
    $filters: RnPRoleContextDetailFilterInput
    $searchText: String
    $includeRnpRoles: Boolean = true
  ) {
    node(id: $roleId, type: RNP_ROLE) {
      ... on RnPRole {
        id
        assignedUsers(
          first: $first
          after: $after
          orderBy: $orderBy
          orderByDirection: $orderByDirection
          filters: $filters
          searchText: $searchText
        ) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              type
              email
              firstName
              lastName
              middleName
              profileImage
              isArchivedV2(filters: { academicYearId: $academicYearId })
              rnpRoleContextDetail(filters: { roleModule: ["BEHAVIOUR"] })
                @include(if: $includeRnpRoles) {
                id
                role {
                  id
                  label
                }
                contextType
                context {
                  id
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getACLPermissionEnabledRolesQuery = gql`
  query getEnabledRoles(
    $ids: [ID!]!
    $assignedUsersFilters: RnPRoleContextDetailFilterInput
  ) {
    multiNode(ids: $ids, type: ACL_CONTROL_PARAMETER) {
      ... on ACLPermission {
        id
        uid
        type
        enabledRoles(levels: [ACCOUNT, PROGRAMME]) {
          id
          label
          globalLevel
          assignedUsers(
            filters: $assignedUsersFilters
            orderBy: FULL_NAME
            orderByDirection: ASC
          ) {
            totalCount
          }
        }
      }
    }
  }
`;

export const getAccountRoleAssignedUsersCount = gql`
  query getTeacherFeed(
    $organizationId: ID!
    $filters: OrgConnectionFilterInput
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        staff(filters: $filters) {
          userCount {
            role
            totalCount
          }
        }
      }
    }
  }
`;

export const getStaffIdsQuery = gql`
  query getStaffIds($organizationId: ID!, $filters: OrgConnectionFilterInput) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        staff(filters: $filters) {
          edges {
            node {
              id
            }
          }
        }
      }
    }
  }
`;

export const getPermittedStaffMembersQuery = gql`
  query getPermittedStaffMembers(
    $organizationId: ID!
    $input: PermittedUsersInput!
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        permittedUsers(input: $input) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              ... on Staff {
                id
                firstName
                middleName
                lastName
                profileImage
                preferredName
                pronouns
                prefix
                suffix
                email
                type
                rnpRoleContextDetail(filters: { roleModule: ["BEHAVIOUR"] }) {
                  id
                  role {
                    id
                    label
                  }
                  contextType
                  context {
                    id
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getAssociatedUsersQuery = gql`
  query getAssociatedUsers(
    $input: FetchAssociatedUsersInput!
    $roleFilters: RnPRoleContextDetailFilterInput
  ) {
    platform {
      fetchAssociatedUsers(input: $input) {
        userId
        associatedUsers {
          ... on Staff {
            id
            rnpRoleContextDetail(filters: $roleFilters) {
              id
              role {
                id
                label
              }
              contextType
              context {
                id
              }
            }
          }
        }
      }
    }
  }
`;
